// src/components/KilnRegistration.js
import React, { useState } from 'react';

export default function KilnRegistration({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    kiln_type: 'Earth Kiln',
    gps_lat: '',
    gps_lng: '',
    farm_gps_lat: '',
    farm_gps_lng: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [error, setError] = useState('');

  const API_BASE = process.env.REACT_APP_API_URL || '';

  const kilnTypes = [
    'Earth Kiln',
    'Stone Kiln',
    'Semi-Permanent Kiln',
    'Permanent Kiln'
  ];

  const getCurrentLocation = (field) => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (field === 'kiln') {
          setFormData(prev => ({
            ...prev,
            gps_lat: position.coords.latitude.toFixed(6),
            gps_lng: position.coords.longitude.toFixed(6)
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            farm_gps_lat: position.coords.latitude.toFixed(6),
            farm_gps_lng: position.coords.longitude.toFixed(6)
          }));
        }
        setGettingLocation(false);
      },
      (err) => {
        setError('Unable to get location: ' + err.message);
        setGettingLocation(false);
      },
      { enableHighAccuracy: true }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.gps_lat || !formData.gps_lng) {
      setError('Kiln GPS location is required');
      return;
    }
    if (!formData.farm_gps_lat || !formData.farm_gps_lng) {
      setError('Farm GPS location is required');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/kilns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          gps_lat: parseFloat(formData.gps_lat),
          gps_lng: parseFloat(formData.gps_lng),
          farm_gps_lat: parseFloat(formData.farm_gps_lat),
          farm_gps_lng: parseFloat(formData.farm_gps_lng)
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to register kiln');
      }

      const data = await res.json();
      alert(`Kiln registered successfully!\nKiln ID: ${data.kiln_id}`);
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modal: {
      background: '#1a3327',
      padding: '24px',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '90vh',
      overflow: 'auto',
      border: '1px solid #2d5a45'
    },
    title: {
      color: '#4ade80',
      fontSize: '20px',
      marginBottom: '20px'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      color: '#888',
      fontSize: '12px',
      marginBottom: '6px',
      textTransform: 'uppercase'
    },
    input: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      border: '1px solid #2d5a45',
      background: '#0f2419',
      color: 'white',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      border: '1px solid #2d5a45',
      background: '#0f2419',
      color: 'white',
      fontSize: '14px',
      cursor: 'pointer'
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px'
    },
    button: {
      padding: '12px 24px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold',
      width: '100%'
    },
    buttonPrimary: {
      background: '#4ade80',
      color: '#0f2419'
    },
    buttonSecondary: {
      background: '#2d5a45',
      color: 'white'
    },
    buttonLocation: {
      background: '#3b82f6',
      color: 'white',
      marginTop: '8px'
    },
    error: {
      background: '#fee2e2',
      color: '#dc2626',
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '14px'
    },
    section: {
      background: '#0f2419',
      padding: '16px',
      borderRadius: '8px',
      marginBottom: '16px',
      border: '1px solid #2d5a45'
    },
    sectionTitle: {
      color: '#4ade80',
      fontSize: '14px',
      marginBottom: '12px',
      fontWeight: '500'
    },
    buttonRow: {
      display: 'flex',
      gap: '12px',
      marginTop: '20px'
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <h2 style={styles.title}>Register Biochar Earth Kiln</h2>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Kiln Type */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Kiln Type</label>
            <select 
              style={styles.select}
              value={formData.kiln_type}
              onChange={e => setFormData(prev => ({ ...prev, kiln_type: e.target.value }))}
            >
              {kilnTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Kiln GPS */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>📍 Kiln Location (GPS)</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Latitude</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="-0.3031"
                  value={formData.gps_lat}
                  onChange={e => setFormData(prev => ({ ...prev, gps_lat: e.target.value }))}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Longitude</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="36.0800"
                  value={formData.gps_lng}
                  onChange={e => setFormData(prev => ({ ...prev, gps_lng: e.target.value }))}
                />
              </div>
            </div>
            <button
              type="button"
              style={{...styles.button, ...styles.buttonLocation}}
              onClick={() => getCurrentLocation('kiln')}
              disabled={gettingLocation}
            >
              {gettingLocation ? 'Getting Location...' : '📍 Use Current Location for Kiln'}
            </button>
          </div>

          {/* Farm GPS */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>🌾 Farm Location (GPS)</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Latitude</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="-0.3025"
                  value={formData.farm_gps_lat}
                  onChange={e => setFormData(prev => ({ ...prev, farm_gps_lat: e.target.value }))}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Longitude</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="36.0795"
                  value={formData.farm_gps_lng}
                  onChange={e => setFormData(prev => ({ ...prev, farm_gps_lng: e.target.value }))}
                />
              </div>
            </div>
            <button
              type="button"
              style={{...styles.button, ...styles.buttonLocation}}
              onClick={() => getCurrentLocation('farm')}
              disabled={gettingLocation}
            >
              {gettingLocation ? 'Getting Location...' : '📍 Use Current Location for Farm'}
            </button>
          </div>

          {/* Notes */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Notes (Optional)</label>
            <textarea
              style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
              placeholder="Any additional notes about the kiln..."
              value={formData.notes}
              onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            />
          </div>

          {/* Buttons */}
          <div style={styles.buttonRow}>
            <button
              type="button"
              style={{...styles.button, ...styles.buttonSecondary}}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{...styles.button, ...styles.buttonPrimary}}
              disabled={loading}
            >
              {loading ? 'Registering...' : 'Register Kiln'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}